import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, ArrowRight } from "lucide-react";
import { Field, SelectField } from "@/components/site/FormControls";
import { PriceBreakdown } from "./PriceBreakdown";
import { useQuote, CheckList, toggleIn } from "./useQuote";

export const PriceCalculator = ({ services, options }) => {
  const navigate = useNavigate();
  const [f, setF] = useState({ service_id: "", sqft: "", property_type: "", units: 1, zip: "", additional_services: [], special_conditions: [] });
  const set = (e) => setF({ ...f, [e.target.name]: e.target.value });
  const quote = useQuote(f);

  const schedule = () => {
    const q = new URLSearchParams({ service: f.service_id, sqft: f.sqft, zip: f.zip, units: f.units, property_type: f.property_type });
    navigate(`/property-inspections/book?${q.toString()}`);
  };

  return (
    <div data-testid="price-calculator" className="grid gap-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_24px_60px_-40px_rgba(11,19,43,0.4)] sm:p-10 lg:grid-cols-[1.3fr_1fr]">
      <div>
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-gold/10 p-3"><Calculator className="h-5 w-5 text-gold" /></div>
          <h3 className="font-display text-2xl font-bold text-[#0B132B]">Instant Estimate</h3>
        </div>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-medium text-slate-600">Inspection type <span className="text-gold">*</span></label>
            <select data-testid="calc-service" name="service_id" value={f.service_id} onChange={set}
              className="w-full rounded-lg border border-slate-300 bg-[#FAF8F1] px-4 py-2.5 text-sm text-[#0B132B] focus:border-gold focus:outline-none">
              <option value="">Select an inspection…</option>
              {services.map((s) => <option key={s.id} value={s.id}>{s.name} (from ${s.base_price})</option>)}
            </select>
          </div>
          <Field label="Square footage" name="sqft" type="number" value={f.sqft} onChange={set} half required testid="calc-sqft" />
          <Field label="ZIP code" name="zip" value={f.zip} onChange={set} half testid="calc-zip" />
          <SelectField label="Property type" name="property_type" value={f.property_type} onChange={set} options={options?.property_types || []} half testid="calc-property-type" />
          <Field label="Number of units" name="units" type="number" value={f.units} onChange={set} half testid="calc-units" />
          <CheckList title="Additional services" items={options?.additional_services} priceKey="price" selected={f.additional_services}
            onToggle={(n) => setF({ ...f, additional_services: toggleIn(f.additional_services, n) })} testid="calc-addon" />
          <CheckList title="Special property conditions" items={options?.special_conditions} priceKey="surcharge" selected={f.special_conditions}
            onToggle={(n) => setF({ ...f, special_conditions: toggleIn(f.special_conditions, n) })} testid="calc-condition" />
        </div>
      </div>
      <div className="flex flex-col justify-between gap-6">
        {quote ? <PriceBreakdown quote={quote} testid="calc-breakdown" /> : (
          <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-400">
            Choose an inspection and enter the square footage to see your itemized estimate.
          </div>
        )}
        <button type="button" onClick={schedule} disabled={!f.service_id} data-testid="calc-schedule-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-sm font-semibold text-[#0B132B] transition-colors hover:bg-[#E5C158] disabled:opacity-50">
          Schedule a Property Inspection <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
